import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { InputAdornment, TextField, Tooltip } from '@mui/material';
import { NumericFormat } from 'react-number-format';

import Icon from './Icon';

import './CNumericField.css';

const CNumericField = ({
    label,
    maxLength,
    minLength,
    currency,
    returnCurrencyValue,
    tooltipIcon,
    tooltipText,
    tooltipFadeDelay,
    helperText,
    isError,
    disabled,
    loadValue,
    onChange,
    plainNumber = false,
    allowLeadingZeros = false,
    required = false
}) => {
    const [value, setValue] = useState('');
    const [floatValue, setFloatValue] = useState(0);

    const [numericFieldStates, setNumericFieldStates] = useState({
        label: label || '',
        maxLength: maxLength || 12,
        returnCurrencyValue: returnCurrencyValue || false,
        tooltipIcon: tooltipIcon || '',
        tooltipText: tooltipText || '',
        tooltipFadeDelay: tooltipFadeDelay || 500,
        helperText: helperText !== undefined ? helperText : '',
        isError: isError !== undefined ? isError : false,
        disabled: disabled !== undefined ? disabled : false,
    });

    const prefix = useMemo(() => {
        if (plainNumber || !currency) return '';
        try {
            return new Intl.NumberFormat('en-US', { style: 'currency', currency, minimumFractionDigits: 0 })
                .formatToParts(0)
                .find(part => part.type === 'currency')?.value || '';
        } catch {
            return '';
        }
    }, [plainNumber, currency]);

    useEffect(() => {
        setNumericFieldStates((prevState) => ({
            ...prevState,
            label: label !== undefined ? label : prevState.label,
            maxLength: maxLength !== undefined ? maxLength : prevState.maxLength,
            returnCurrencyValue: returnCurrencyValue !== undefined ? returnCurrencyValue : prevState.returnCurrencyValue,
            tooltipIcon: tooltipIcon !== undefined ? tooltipIcon : prevState.tooltipIcon,
            tooltipText: tooltipText !== undefined ? tooltipText : prevState.tooltipText,
            tooltipFadeDelay: tooltipFadeDelay !== undefined ? tooltipFadeDelay : prevState.tooltipFadeDelay,
            helperText: helperText !== undefined ? helperText : prevState.helperText,
            isError: isError !== undefined ? isError : prevState.isError,
            disabled: disabled !== undefined ? disabled : prevState.disabled,
        }));
    }, [label, maxLength, returnCurrencyValue, tooltipIcon, tooltipText, tooltipFadeDelay, helperText, isError, disabled]);

    useEffect(() => {
        if (loadValue !== undefined && loadValue !== null) {
            setValue(loadValue);
        }
    }, [loadValue]);

    const handleChange = (values) => {
        const { formattedValue, value: rawValue, floatValue } = values;
        if (!rawValue) {
            onChange(allowLeadingZeros ? '' : 0);
            setValue('');
            setFloatValue(0);
        } else {
            if (allowLeadingZeros) {
                onChange(rawValue);
            } else if (plainNumber) {
                onChange(floatValue);
            } else {
                const pointsValue = floatValue * 100;
                if (numericFieldStates.returnCurrencyValue) {
                    onChange(floatValue);
                } else {
                    onChange(pointsValue);
                }
            }

            setValue(formattedValue);
            setFloatValue(floatValue);
        }
    };

    const handleClearInput = () => {
        onChange(allowLeadingZeros ? '' : 0);
        setValue('');
        setFloatValue(0);
    }

    return (
        <div className='c-numeric-field'>
            <NumericFormat
                value={value}
                decimalScale={plainNumber ? 0 : 2}
                fixedDecimalScale={!plainNumber}
                thousandSeparator={plainNumber ? false : " "}
                prefix={prefix}
                allowLeadingZeros={allowLeadingZeros}
                onValueChange={handleChange}
                customInput={TextField}
                label={numericFieldStates.label}
                disabled={numericFieldStates.disabled}
                required={required}
                allowNegative={false}
                InputProps={{
                    endAdornment: (
                        <div className='end-adornments'>
                            {/* <InputAdornment position="end">
                                <Icon name="close" size={16} className='clear-icon' onClick={handleClearInput} />
                            </InputAdornment> */}
                            {numericFieldStates.tooltipIcon ? (
                                <Tooltip
                                    placement="top"
                                    title={numericFieldStates.tooltipText}
                                    enterDelay={200}
                                    leaveDelay={numericFieldStates.tooltipFadeDelay}
                                    arrow
                                >
                                    <div>
                                        <Icon name={numericFieldStates.tooltipIcon} size={16} className='tooltip-icon' />
                                    </div>
                                </Tooltip>
                            ) : null}
                        </div>
                    ),
                    inputProps: {
                        maxLength: numericFieldStates.maxLength,
                        minLength: minLength
                    }
                }}
                helperText={numericFieldStates.helperText}
                error={numericFieldStates.isError}
            />
        </div>
    )
}

CNumericField.propTypes = {
    label: PropTypes.string,
    maxLength: PropTypes.number,
    currency: PropTypes.string,
    returnCurrencyValue: PropTypes.bool,
    tooltipIcon: PropTypes.string,
    tooltipText: PropTypes.string,
    tooltipFadeDelay: PropTypes.number,
    helperText: PropTypes.string,
    isError: PropTypes.bool,
    disabled: PropTypes.bool,
    loadValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onChange: PropTypes.func.isRequired,
    plainNumber: PropTypes.bool,
    minLength: PropTypes.number,
    allowLeadingZeros: PropTypes.bool,
    required: PropTypes.bool
};

export default CNumericField;